import { useState } from "react";
import { Button } from "components/button/Button";
import { Attachment } from "models/dashboard/dashboard";
import { Loader } from "components/loader/Loader";
import { splitName } from "common/splitName/splitName";
import { Icon } from "components/icon/Icon";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import CreateThread from "views/inbox/containers/createThread/CreateThread";
import Message from "../message/Message";
import { useRootStore } from "store";

import "./ThreadMessageList.scss";

type Props = {
  onReply: (
    body: string,
    users: string[],
    threadId: string,
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => Promise<void>;
  onDeleteMessage: (messageId: string, messageType: string) => void;
  onOpenDocument: (item: { id: string; name: string }) => void;
};

export default function ThreadMessageList({
  onReply,
  onDeleteMessage,
  onOpenDocument
}: Props) {
  const {currentThread: thread} = useRootStore((state) => state.inbox);
  const {workingCustomer:customer} = useRootStore((state) => state.customers)
  const { user } = useRootStore((state) => state);

  const { internalUsers } = customer;
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);

  if (!thread) {
    return <></>;
  }

  if (!thread.messages) {
    return <></>;
  }

  const canDeleteMessage = !!user.userInfo && user.userInfo.type === "Internal";

  const handleOnSend = async (
    title: string,
    body: string,
    users: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    setIsSending(true);

    await onReply(body, users, thread.threadId, attachments, filesToUpload)
      .then(() => {
        setIsEditing(false);
        setIsSending(false);
      })
      .catch(() => {
        setIsSending(false);
      });
  };

  const handleOnReply = () => {
    setIsEditing(true);
  };

  const handleOnCancel = () => {
    setIsEditing(false);
  };

  const subscribers = thread.subscribers || [];

  const subscriberIds = subscribers.map((subscriber) => {
    return subscriber.id;
  });
  const currentUserId = user.userInfo ? user.userInfo.id : "";
  const absentSubscribers = internalUsers.filter(
    (inernalUser) =>
      inernalUser.id !== currentUserId &&
      subscriberIds.includes(inernalUser.id) &&
      inernalUser.outOfOfficeMessage
  );

  return (
    <div className="threadMessageListWrapper">
      <div className="threadHeader">
        <div className="threadTitle">
          <span title={thread.title}>{thread.title}</span>
        </div>

        <div>
          {!isEditing && (
            <Button
              size={"sm"}
              onClick={handleOnReply}
              className={"mb-4 no-print"}
            >
              <Icon name="reply-all" className={"mr-3"} />
              <span>Svara</span>
            </Button>
          )}

          {isEditing && (
            <Button
              size={"sm"}
              onClick={handleOnCancel}
              className={"mb-3 no-print"}
            >
              <Icon name="trash" className={"mr-3"} />
              <span>Släng utkast</span>
            </Button>
          )}
        </div>
      </div>

      <div className="sender">
        <div className="mr-2">Deltagare: </div>
        <div className="d-flex flex-wrap">
          {subscribers.slice(0, 5).map((s, i, arr) => (
            <span key={i} className="text-nowrap mr-2 mb-1">
              {`${s.givenName} ${s.surName}`}
              {i !== arr.length - 1 && ", "}
            </span>
          ))}

          <span className="text-nowrap">
            {subscribers.length > 5 && (
              <> + {subscribers.length - 5} andra mottagare</>
            )}
          </span>
        </div>
      </div>
      {!isEditing && absentSubscribers.length > 0 && (
        <div className="mt-4">
          <label className="mb-3">Frånvaromeddelanden</label>

          {absentSubscribers.map((u) => {
            return (
              <div className="mt-0" key={u.id}>
                <b>
                  {u.givenName} {u.surName}
                </b>
                :
                <div className="marginTop">
                  {u.outOfOfficeMessage.split("\n").map((item, key) => {
                    return (
                      <span key={key}>
                        {item}
                        <br />
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="message-wrapper">
        {isEditing && (
          <div className={"mb-4"}>
            <CreateThread
              isSending={isSending}
              defaultRecipients={thread.subscribers}
              onSend={handleOnSend}
              thread={thread}
            />
          </div>
        )}

        {isSending && <Loader />}

        <hr />

        {thread.messages.map((message) => {
          const senderName = splitName(message.formatedFrom);

          return (
            <Message
              key={message.messageId}
              canDeleteMessage={canDeleteMessage}
              message={message}
              sender={senderName}
              thread={thread}
              onDeleteMessage={onDeleteMessage}
              onOpenDocument={onOpenDocument}
            />
          );
        })}
      </div>
    </div>
  );
}
